import { useState, useEffect, useContext, useCallback } from 'react';
import {
    Box, Typography, Stack, Button, Sheet, Table, Chip,
    IconButton, Alert, CircularProgress, Input,
    Modal, ModalDialog, ModalClose, DialogContent,
    FormControl, FormLabel, Select, Option
} from '@mui/joy';
import { LayoutContext } from '../context';
import { APIGetAllUsers, APIUpdateUser, APIDeleteUser, APICreateUser, APIGrantUnlimited, APISetUserLimit, APIToggleRussianAccess, APIGetUserActivity, APIGetPaidUsers, APIGetPaidFeedbackAdmin, APIGetPaymentsAnalytics } from '../api';
import { PencilSimple, Trash, Plus, Gift, Sliders, MagnifyingGlass, Users, Translate, CreditCard, CaretLeft, CaretRight, Star, ChartBar } from "@phosphor-icons/react";
import UserModal from '../components/modals/UserModal';
import PaymentsAnalytics from '../components/admin/PaymentsAnalytics';

export default function UsersPage() {
    const { token, user: currentUser } = useContext(LayoutContext);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);

    // Модальное окно
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(null);

    // Модалка безлимита
    const [grantModalOpen, setGrantModalOpen] = useState(false);
    const [grantEmail, setGrantEmail] = useState('');
    const [grantDays, setGrantDays] = useState(30);
    const [grantLoading, setGrantLoading] = useState(false);
    const [grantResult, setGrantResult] = useState(null);

    // Модалка лимитов
    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const [limitEmail, setLimitEmail] = useState('');
    const [limitTests, setLimitTests] = useState(25);
    const [limitModules, setLimitModules] = useState(10);
    const [limitLoading, setLimitLoading] = useState(false);
    const [limitResult, setLimitResult] = useState(null);

    // Модалка активности
    const [activityModalOpen, setActivityModalOpen] = useState(false);
    const [activityData, setActivityData] = useState(null);
    const [activityLoading, setActivityLoading] = useState(false);

    // Модалка платных юзеров
    const [paidModalOpen, setPaidModalOpen] = useState(false);
    const [paidUsers, setPaidUsers] = useState([]);
    const [grantedUsers, setGrantedUsers] = useState([]);
    const [paidStats, setPaidStats] = useState(null);
    const [paidLoading, setPaidLoading] = useState(false);
    const [paidTab, setPaidTab] = useState('paid');

    // Модалка «Анализ» — оплаты по месяцам
    const [analyticsOpen, setAnalyticsOpen] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsError, setAnalyticsError] = useState('');
    const [reviews, setReviews] = useState([]);
    const [reviewsAvg, setReviewsAvg] = useState(0);
    const [reviewsLoading, setReviewsLoading] = useState(false);

    const fetchUsers = useCallback(async (p = page, s = search) => {
        try {
            setLoading(true);
            const response = await APIGetAllUsers(token, p, 50, s);
            if (response.success) {
                setUsers(response.users);
                setPagination(response.pagination);
            } else {
                setError(response.error);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Ошибка при загрузке пользователей');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUsers(1, '');
    }, []);

    // Debounced search
    const handleSearchChange = (value) => {
        setSearch(value);
        if (searchTimeout) clearTimeout(searchTimeout);
        setSearchTimeout(setTimeout(() => {
            setPage(1);
            fetchUsers(1, value);
        }, 400));
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchUsers(newPage, search);
    };

    const handleCreateUser = () => {
        setEditingUser(null);
        setUserModalOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setUserModalOpen(true);
    };

    const handleDeleteUser = async (user) => {
        if (!window.confirm(`Вы уверены, что хотите удалить пользователя ${user.email}?`)) {
            return;
        }

        setDeleteLoading(user.id);
        try {
            const response = await APIDeleteUser(token, user.id);
            if (response.success) {
                await fetchUsers(page, search);
            } else {
                alert(response.error);
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Ошибка при удалении пользователя');
        } finally {
            setDeleteLoading(null);
        }
    };

    const handleSaveUser = async (userData) => {
        setSaveLoading(true);
        try {
            let response;
            if (editingUser) {
                response = await APIUpdateUser(token, editingUser.id, userData);
            } else {
                response = await APICreateUser(token, userData);
            }

            if (response.success) {
                setUserModalOpen(false);
                await fetchUsers(page, search);
            } else {
                alert(response.error);
            }
        } catch (error) {
            console.error('Error saving user:', error);
            alert('Ошибка при сохранении пользователя');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleGrantUnlimited = async () => {
        if (!grantEmail.trim()) return;
        setGrantLoading(true);
        setGrantResult(null);
        try {
            const response = await APIGrantUnlimited(token, grantEmail.trim(), grantDays);
            if (response.success) {
                setGrantResult({ type: 'success', message: response.message });
                await fetchUsers(page, search);
            } else {
                setGrantResult({ type: 'error', message: response.error });
            }
        } catch (error) {
            setGrantResult({
                type: 'error',
                message: error.response?.data?.error || 'Ошибка при выдаче безлимита'
            });
        } finally {
            setGrantLoading(false);
        }
    };

    const handleSetLimit = async () => {
        if (!limitEmail.trim()) return;
        setLimitLoading(true);
        setLimitResult(null);
        try {
            const response = await APISetUserLimit(token, limitEmail.trim(), limitTests, limitModules);
            if (response.success) {
                setLimitResult({ type: 'success', message: response.message });
                await fetchUsers(page, search);
            } else {
                setLimitResult({ type: 'error', message: response.error });
            }
        } catch (error) {
            setLimitResult({
                type: 'error',
                message: error.response?.data?.error || 'Ошибка при изменении лимита'
            });
        } finally {
            setLimitLoading(false);
        }
    };

    const handleViewActivity = async (user) => {
        setActivityLoading(true);
        setActivityModalOpen(true);
        setActivityData(null);
        try {
            const data = await APIGetUserActivity(token, user.id);
            setActivityData(data);
        } catch (e) {
            setActivityData({ error: e.message });
        } finally {
            setActivityLoading(false);
        }
    };

    const handleShowPaidUsers = async () => {
        setPaidModalOpen(true);
        setPaidLoading(true);
        try {
            const res = await APIGetPaidUsers(token);
            if (res.success) {
                setPaidUsers(res.paid_users || []);
                setGrantedUsers(res.granted_users || []);
                setPaidStats(res.stats || null);
            }
        } catch (e) {
            console.error('Error fetching paid users:', e);
        } finally {
            setPaidLoading(false);
        }
    };

    const handleShowAnalytics = async () => {
        setAnalyticsOpen(true);
        setAnalyticsLoading(true);
        setAnalyticsError('');
        try {
            const res = await APIGetPaymentsAnalytics(token);
            if (res.success) {
                setAnalyticsData(res);
            } else {
                setAnalyticsError(res.error || 'Не удалось загрузить');
            }
        } catch (e) {
            console.error('Error fetching payments analytics:', e);
            setAnalyticsError('Не удалось загрузить аналитику. Попробуйте ещё раз.');
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const openReviewsTab = async () => {
        setPaidTab('reviews');
        setReviewsLoading(true);
        try {
            const res = await APIGetPaidFeedbackAdmin(token);
            if (res.success) {
                setReviews(res.reviews || []);
                setReviewsAvg(res.avg_rating || 0);
            }
        } catch (e) {
            console.error('Error fetching reviews:', e);
        } finally {
            setReviewsLoading(false);
        }
    };

    const handleToggleRussian = async (user) => {
        try {
            const response = await APIToggleRussianAccess(token, user.id);
            if (response.success) {
                setUsers(prev => prev.map(u =>
                    u.id === user.id ? { ...u, russian_content_access: response.russian_content_access } : u
                ));
            }
        } catch (error) {
            console.error('Error toggling russian access:', error);
        }
    };

    // Платежи modal: производные списки.
    // "APEX 12m" = реальная оплата со сроком >= 360 дней (годовой тариф:
    // 24 900 / 29 900 / 395-дн бонус). days приходит с бэка (custom_days || rate.days).
    const twelveMonthUsers = paidUsers.filter(u => (u.days || 0) >= 360);
    const twelveMonthRevenue = twelveMonthUsers.reduce((s, u) => s + (u.amount || 0), 0);
    const paymentRows = paidTab === 'granted' ? grantedUsers
        : paidTab === 'twelve' ? twelveMonthUsers
        : paidUsers;
    // paid и twelve — реальные оплаты (показываем источник); granted — нет.
    const paymentTabIsReal = paidTab !== 'granted';

    if (currentUser?.role !== 'admin') {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Alert color="danger" variant="soft">
                    <Typography level="h4">Доступ запрещен</Typography>
                    <Typography>У вас недостаточно прав для просмотра этой страницы</Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
                {/* Заголовок + кнопки */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography level="h2">Пользователи</Typography>
                        {pagination && (
                            <Chip
                                variant="soft"
                                color="neutral"
                                size="lg"
                                startDecorator={<Users size={18} />}
                                sx={{ fontWeight: 600 }}
                            >
                                {pagination.total_count}
                            </Chip>
                        )}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            color="success"
                            startDecorator={<CreditCard size={18} />}
                            onClick={handleShowPaidUsers}
                        >
                            Платные юзеры
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            startDecorator={<ChartBar size={18} />}
                            onClick={handleShowAnalytics}
                        >
                            Анализ
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            startDecorator={<Sliders size={18} />}
                            onClick={() => {
                                setLimitEmail('');
                                setLimitTests(25);
                                setLimitModules(10);
                                setLimitResult(null);
                                setLimitModalOpen(true);
                            }}
                        >
                            Изменить лимит
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startDecorator={<Gift size={18} />}
                            onClick={() => {
                                setGrantEmail('');
                                setGrantDays(30);
                                setGrantResult(null);
                                setGrantModalOpen(true);
                            }}
                        >
                            Дать безлимит
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            startDecorator={<Plus size={18} />}
                            onClick={handleCreateUser}
                        >
                            Создать
                        </Button>
                    </Stack>
                </Box>

                {/* Поиск */}
                <Input
                    placeholder="Поиск по email, имени или фамилии..."
                    startDecorator={<MagnifyingGlass size={18} />}
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    sx={{ maxWidth: 450 }}
                />

                {error && (
                    <Alert color="danger" variant="soft">
                        {error}
                    </Alert>
                )}

                {/* Таблица пользователей */}
                <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto' }}>
                    {loading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <CircularProgress />
                            <Typography level="body-sm" sx={{ mt: 2 }}>
                                Загрузка...
                            </Typography>
                        </Box>
                    ) : users.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography level="body-md" color="neutral">
                                {search ? 'Ничего не найдено' : 'Пользователи не найдены'}
                            </Typography>
                        </Box>
                    ) : (
                        <Table stripe="odd" hoverRow sx={{ '& th': { fontWeight: 600, fontSize: '0.8rem' } }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 50 }}>ID</th>
                                    <th>Email</th>
                                    <th>Имя</th>
                                    <th>Тариф</th>
                                    <th>Роль</th>
                                    <th>Лимит</th>
                                    <th>Статус</th>
                                    <th>Решено</th>
                                    <th>Источник</th>
                                    <th>Регистрация</th>
                                    <th style={{ width: 120 }}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => {
                                    // Фиолетовая подсветка строки для free_trial-источника
                                    // (пришли по специальной ссылке из TG-канала).
                                    const isFreeTrial = user.referral_source === 'free_trial';
                                    return (
                                    <tr
                                        key={user.id}
                                        style={isFreeTrial ? {
                                            backgroundColor: 'rgba(168, 85, 247, 0.08)',
                                            borderLeft: '3px solid #A855F7',
                                        } : undefined}
                                    >
                                        <td>
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                {user.id}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-sm" fontWeight={600}>
                                                {user.email}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-sm">
                                                {user.first_name} {user.last_name}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Chip
                                                size="sm"
                                                variant="soft"
                                                color={
                                                    user.product_code === 'qbank_only' ? 'warning'
                                                    : user.product_code ? 'success'
                                                    : (user.rate?.plan_type === 'plan1' ? 'success' : 'neutral')
                                                }
                                            >
                                                {user.product_label || user.rate?.name || 'Бесплатный'}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Chip
                                                size="sm"
                                                variant="soft"
                                                color={user.role === 'admin' ? 'danger' : 'primary'}
                                            >
                                                {user.role === 'admin' ? 'Админ' : 'Юзер'}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Stack spacing={0}>
                                                <Typography level="body-xs" fontWeight={600}>
                                                    {user.rate?.plan_type === 'plan1' ? '∞' : user.daily_test_limit || 25} тестов
                                                </Typography>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                    {user.rate?.plan_type === 'plan1' ? '∞' : user.daily_module_limit || 10} модулей
                                                </Typography>
                                            </Stack>
                                        </td>
                                        <td>
                                            <Chip
                                                size="sm"
                                                variant={user.is_active ? 'soft' : 'outlined'}
                                                color={user.is_active ? 'success' : 'neutral'}
                                            >
                                                {user.is_active ? 'Актив' : 'Неактив'}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Stack spacing={0}>
                                                <Typography level="body-sm" fontWeight={600}>
                                                    {user.answers_count || 0}
                                                </Typography>
                                                {user.answers_count > 0 && (
                                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                        {Math.round((user.correct_answers_count || 0) / user.answers_count * 100)}% верно
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </td>
                                        <td>
                                            {user.referral_source ? (
                                                <Chip
                                                    size="sm"
                                                    variant={isFreeTrial ? 'solid' : 'soft'}
                                                    sx={isFreeTrial ? {
                                                        bgcolor: '#A855F7',
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                    } : undefined}
                                                    color={isFreeTrial ? undefined : 'neutral'}
                                                >
                                                    {user.referral_source}
                                                </Chip>
                                            ) : (
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>—</Typography>
                                            )}
                                        </td>
                                        <td>
                                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                                {new Date(user.date_create).toLocaleDateString('ru-RU')}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Stack direction="row" spacing={0.5}>
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() => handleViewActivity(user)}
                                                    title="Активность"
                                                >
                                                    <Sliders size={18} />
                                                </IconButton>
                                                <IconButton
                                                    size="sm"
                                                    variant={user.russian_content_access ? 'soft' : 'plain'}
                                                    color={user.russian_content_access ? 'success' : 'neutral'}
                                                    onClick={() => handleToggleRussian(user)}
                                                    title={user.russian_content_access ? 'RU доступ включен' : 'RU доступ выключен'}
                                                >
                                                    <Translate size={18} />
                                                </IconButton>
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() => handleEditUser(user)}
                                                >
                                                    <PencilSimple size={18} />
                                                </IconButton>
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="danger"
                                                    onClick={() => handleDeleteUser(user)}
                                                    disabled={deleteLoading === user.id || user.id === currentUser.id}
                                                    loading={deleteLoading === user.id}
                                                >
                                                    <Trash size={18} />
                                                </IconButton>
                                            </Stack>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Sheet>

                {/* Пагинация */}
                {pagination && pagination.total_pages > 1 && (
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                        <IconButton
                            variant="outlined"
                            size="sm"
                            disabled={!pagination.has_previous}
                            onClick={() => handlePageChange(page - 1)}
                        >
                            <CaretLeft size={20} />
                        </IconButton>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            Стр. {pagination.current_page} из {pagination.total_pages}
                        </Typography>
                        <IconButton
                            variant="outlined"
                            size="sm"
                            disabled={!pagination.has_next}
                            onClick={() => handlePageChange(page + 1)}
                        >
                            <CaretRight size={20} />
                        </IconButton>
                    </Stack>
                )}
            </Stack>

            {/* Модальное окно пользователя */}
            <UserModal
                open={userModalOpen}
                user={editingUser}
                onClose={() => setUserModalOpen(false)}
                onSave={handleSaveUser}
                loading={saveLoading}
            />

            {/* Модалка выдачи безлимита */}
            <Modal open={grantModalOpen} onClose={() => setGrantModalOpen(false)}>
                <ModalDialog sx={{ width: { xs: '95%', sm: 420 }, borderRadius: 'xl' }}>
                    <ModalClose />
                    <DialogContent>
                        <Stack spacing={2.5}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Gift size={20} color="#B84C31" />
                                <Typography level="title-lg">Дать безлимит</Typography>
                            </Stack>

                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                Выдать безлимит тестов и модулей пользователю на указанное количество дней.
                            </Typography>

                            {grantResult && (
                                <Alert
                                    color={grantResult.type === 'success' ? 'success' : 'danger'}
                                    variant="soft"
                                    size="sm"
                                >
                                    {grantResult.message}
                                </Alert>
                            )}

                            <FormControl>
                                <FormLabel>Email пользователя</FormLabel>
                                <Input
                                    type="email"
                                    placeholder="user@example.com"
                                    value={grantEmail}
                                    onChange={(e) => setGrantEmail(e.target.value)}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Количество дней</FormLabel>
                                <Select
                                    value={grantDays}
                                    onChange={(_, v) => setGrantDays(v)}
                                >
                                    <Option value={7}>7 дней</Option>
                                    <Option value={30}>30 дней</Option>
                                    <Option value={60}>60 дней</Option>
                                    <Option value={90}>90 дней</Option>
                                    <Option value={180}>180 дней</Option>
                                    <Option value={365}>365 дней</Option>
                                </Select>
                            </FormControl>

                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                <Button variant="plain" color="neutral" onClick={() => setGrantModalOpen(false)}>
                                    Отмена
                                </Button>
                                <Button
                                    variant="solid"
                                    loading={grantLoading}
                                    disabled={!grantEmail.trim()}
                                    startDecorator={<Gift size={16} />}
                                    onClick={handleGrantUnlimited}
                                >
                                    Выдать безлимит
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>

            {/* Модалка изменения лимита */}
            <Modal open={limitModalOpen} onClose={() => setLimitModalOpen(false)}>
                <ModalDialog sx={{ width: { xs: '95%', sm: 420 }, borderRadius: 'xl' }}>
                    <ModalClose />
                    <DialogContent>
                        <Stack spacing={2.5}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Sliders size={20} color="#B84C31" />
                                <Typography level="title-lg">Изменить лимит</Typography>
                            </Stack>

                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                Установить индивидуальный дневной лимит тестов и модулей для пользователя.
                            </Typography>

                            {limitResult && (
                                <Alert
                                    color={limitResult.type === 'success' ? 'success' : 'danger'}
                                    variant="soft"
                                    size="sm"
                                >
                                    {limitResult.message}
                                </Alert>
                            )}

                            <FormControl>
                                <FormLabel>Email пользователя</FormLabel>
                                <Input
                                    type="email"
                                    placeholder="user@example.com"
                                    value={limitEmail}
                                    onChange={(e) => setLimitEmail(e.target.value)}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Лимит вопросов в день</FormLabel>
                                <Select
                                    value={limitTests}
                                    onChange={(_, v) => setLimitTests(v)}
                                >
                                    <Option value={25}>25 (стандарт)</Option>
                                    <Option value={30}>30</Option>
                                    <Option value={40}>40</Option>
                                    <Option value={50}>50</Option>
                                    <Option value={60}>60</Option>
                                    <Option value={80}>80</Option>
                                    <Option value={100}>100</Option>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Лимит модулей в день</FormLabel>
                                <Select
                                    value={limitModules}
                                    onChange={(_, v) => setLimitModules(v)}
                                >
                                    <Option value={10}>10 (стандарт)</Option>
                                    <Option value={20}>20</Option>
                                    <Option value={30}>30</Option>
                                    <Option value={50}>50</Option>
                                </Select>
                            </FormControl>

                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                <Button variant="plain" color="neutral" onClick={() => setLimitModalOpen(false)}>
                                    Отмена
                                </Button>
                                <Button
                                    variant="solid"
                                    loading={limitLoading}
                                    disabled={!limitEmail.trim()}
                                    startDecorator={<Sliders size={16} />}
                                    onClick={handleSetLimit}
                                >
                                    Сохранить лимит
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>

            {/* Модалка «Анализ» — оплаты по месяцам, стиль Notion */}
            <Modal open={analyticsOpen} onClose={() => setAnalyticsOpen(false)}>
                <ModalDialog sx={{ width: { xs: '96%', md: 920 }, maxWidth: 960, maxHeight: '92vh', overflow: 'auto', borderRadius: 12, bgcolor: '#FAF9F6', p: { xs: 2, md: 3.5 } }}>
                    <ModalClose />
                    <DialogContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                            <ChartBar size={20} color="#37352F" />
                            <Typography level="title-lg" sx={{ color: '#1A1A1A' }}>Анализ оплат</Typography>
                        </Stack>
                        {analyticsLoading && <CircularProgress size="sm" />}
                        {!analyticsLoading && analyticsError && (
                            <Alert color="danger" variant="soft">
                                {analyticsError}
                                <Button size="sm" variant="plain" onClick={handleShowAnalytics} sx={{ ml: 1 }}>Повторить</Button>
                            </Alert>
                        )}
                        {!analyticsLoading && !analyticsError && analyticsData && (
                            <PaymentsAnalytics data={analyticsData} />
                        )}
                    </DialogContent>
                </ModalDialog>
            </Modal>

            {/* Модалка платных юзеров — мини-дашборд */}
            <Modal open={paidModalOpen} onClose={() => setPaidModalOpen(false)}>
                <ModalDialog sx={{ maxWidth: 800, maxHeight: '90vh', overflow: 'auto', borderRadius: 'xl' }}>
                    <ModalClose />
                    <DialogContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <CreditCard size={20} color="#4CAF50" />
                            <Typography level="title-lg">Платежи</Typography>
                        </Stack>

                        {paidLoading && <CircularProgress size="sm" />}

                        {/* Статистика */}
                        {!paidLoading && paidStats && (
                            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                                <Sheet variant="soft" color="success" sx={{ p: 2, borderRadius: 'lg', flex: 1, textAlign: 'center' }}>
                                    <Typography level="h3" sx={{ color: '#4CAF50' }}>
                                        {paidStats.total_revenue?.toLocaleString()} ₽
                                    </Typography>
                                    <Typography level="body-xs">Общая выручка</Typography>
                                </Sheet>
                                <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'lg', flex: 1, textAlign: 'center' }}>
                                    <Typography level="h3">{paidStats.total_paid}</Typography>
                                    <Typography level="body-xs">Оплатили ({paidStats.active_paid} акт.)</Typography>
                                </Sheet>
                                <Sheet variant="soft" color="neutral" sx={{ p: 2, borderRadius: 'lg', flex: 1, textAlign: 'center' }}>
                                    <Typography level="h3">{paidStats.total_granted}</Typography>
                                    <Typography level="body-xs">Подарено ({paidStats.active_granted} акт.)</Typography>
                                </Sheet>
                            </Stack>
                        )}

                        {/* Табы */}
                        {!paidLoading && (
                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                <Button
                                    size="sm"
                                    variant={paidTab === 'paid' ? 'solid' : 'plain'}
                                    color={paidTab === 'paid' ? 'success' : 'neutral'}
                                    onClick={() => setPaidTab('paid')}
                                >
                                    Оплатили ({paidUsers.length})
                                </Button>
                                <Button
                                    size="sm"
                                    variant={paidTab === 'granted' ? 'solid' : 'plain'}
                                    color={paidTab === 'granted' ? 'neutral' : 'neutral'}
                                    onClick={() => setPaidTab('granted')}
                                >
                                    Подарено ({grantedUsers.length})
                                </Button>
                                <Button
                                    size="sm"
                                    variant={paidTab === 'twelve' ? 'solid' : 'plain'}
                                    color={paidTab === 'twelve' ? 'primary' : 'neutral'}
                                    startDecorator={<CreditCard size={15} />}
                                    onClick={() => setPaidTab('twelve')}
                                >
                                    APEX 12m ({twelveMonthUsers.length})
                                </Button>
                                <Button
                                    size="sm"
                                    variant={paidTab === 'reviews' ? 'solid' : 'plain'}
                                    color={paidTab === 'reviews' ? 'warning' : 'neutral'}
                                    startDecorator={<Star size={15} weight="fill" />}
                                    onClick={openReviewsTab}
                                >
                                    Отзывы{reviews.length ? ` (${reviews.length})` : ''}
                                </Button>
                            </Stack>
                        )}

                        {/* Сводка для вкладки APEX 12m */}
                        {!paidLoading && paidTab === 'twelve' && (
                            <Sheet variant="soft" color="primary" sx={{ p: 1.5, borderRadius: 'lg', mb: 2 }}>
                                <Typography level="body-sm">
                                    Купили годовой тариф (12 мес): <b>{twelveMonthUsers.length}</b> чел · сумма{' '}
                                    <b style={{ color: '#4CAF50' }}>{twelveMonthRevenue.toLocaleString()} ₽</b>
                                </Typography>
                            </Sheet>
                        )}

                        {/* Таблица */}
                        {!paidLoading && paidTab !== 'reviews' && paymentRows.length > 0 && (
                            <Table stripe="odd" sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem' } }}>
                                <thead>
                                    <tr>
                                        <th>Юзер</th>
                                        <th>Telegram</th>
                                        <th>Сумма</th>
                                        {paymentTabIsReal && <th>Источник</th>}
                                        <th>Оплата</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentRows.map((pu, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <Stack spacing={0}>
                                                    <Typography level="body-xs" fontWeight={600}>{pu.email}</Typography>
                                                    {(pu.first_name || pu.last_name) && (
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                            {pu.first_name} {pu.last_name}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </td>
                                            <td>
                                                {pu.telegram ? (
                                                    <Typography
                                                        component="a"
                                                        href={`https://t.me/${pu.telegram}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        level="body-xs"
                                                        fontWeight={600}
                                                        sx={{ color: '#0088cc', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                    >
                                                        @{pu.telegram}
                                                    </Typography>
                                                ) : (
                                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>—</Typography>
                                                )}
                                            </td>
                                            <td>
                                                <Typography level="body-sm" fontWeight={600} sx={{ color: paymentTabIsReal ? '#4CAF50' : 'text.secondary' }}>
                                                    {pu.amount > 0 ? `${pu.amount?.toLocaleString()} ₽` : 'Бесплатно'}
                                                </Typography>
                                            </td>
                                            {paymentTabIsReal && (
                                                <td>
                                                    <Chip size="sm" variant="soft" color={pu.source === 'prodamus' ? 'warning' : 'primary'}>
                                                        {pu.source === 'prodamus' ? 'Prodamus' : 'YooKassa'}
                                                    </Chip>
                                                </td>
                                            )}
                                            <td>
                                                <Typography level="body-xs">{pu.payment_date}</Typography>
                                            </td>
                                            <td>
                                                {/* Статус: активен / ждёт активации (paid, нет expires_at) / истёк */}
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color={pu.is_active ? 'success' : (!pu.expires_at ? 'warning' : 'neutral')}
                                                >
                                                    {pu.is_active
                                                        ? `${pu.days_remaining} дн.`
                                                        : (!pu.expires_at ? 'Ждёт активации' : 'Истёк')}
                                                </Chip>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                        {!paidLoading && paidTab !== 'reviews' && paymentRows.length === 0 && (
                            <Typography level="body-sm" color="neutral">Нет данных</Typography>
                        )}

                        {/* Вкладка отзывов платных пользователей (только админ) */}
                        {paidTab === 'reviews' && (
                            <>
                                {reviewsLoading && (
                                    <Typography level="body-sm" color="neutral">Загрузка...</Typography>
                                )}
                                {!reviewsLoading && reviews.length > 0 && (
                                    <>
                                        <Sheet variant="soft" color="warning" sx={{ p: 1.5, borderRadius: 'lg', mb: 2 }}>
                                            <Typography level="body-sm">
                                                Отзывов: <b>{reviews.length}</b> · средняя оценка <b>{reviewsAvg}</b> / 5
                                            </Typography>
                                        </Sheet>
                                        <Table sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem' } }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '22%' }}>Юзер</th>
                                                    <th style={{ width: '11%' }}>Оценка</th>
                                                    <th>Отзыв</th>
                                                    <th style={{ width: '12%' }}>Публикация</th>
                                                    <th style={{ width: '13%' }}>Дата</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reviews.map((r) => (
                                                    <tr key={r.id}>
                                                        <td>
                                                            <Stack spacing={0}>
                                                                <Typography level="body-xs" fontWeight={600}>{r.email}</Typography>
                                                                {r.name && <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{r.name}</Typography>}
                                                            </Stack>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-sm" sx={{ color: '#DA7756', whiteSpace: 'nowrap' }}>
                                                                {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-xs" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                {r.text ? r.text : '—'}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Chip size="sm" variant="soft" color={r.can_publish ? 'success' : 'neutral'}>
                                                                {r.can_publish ? 'Можно' : 'Нет'}
                                                            </Chip>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-xs">{(r.created_at || '').slice(0, 10)}</Typography>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                )}
                                {!reviewsLoading && reviews.length === 0 && (
                                    <Typography level="body-sm" color="neutral">Пока нет отзывов</Typography>
                                )}
                            </>
                        )}
                    </DialogContent>
                </ModalDialog>
            </Modal>

            {/* Модалка активности юзера */}
            <Modal open={activityModalOpen} onClose={() => setActivityModalOpen(false)}>
                <ModalDialog sx={{ maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }}>
                    <ModalClose />
                    <DialogContent>
                        <Typography level="h4" sx={{ mb: 2 }}>Активность пользователя</Typography>
                        {activityLoading && <CircularProgress size="sm" />}
                        {activityData?.error && <Alert color="danger">{activityData.error}</Alert>}
                        {activityData && !activityData.error && (
                            <Stack spacing={1.5}>
                                <Typography level="body-sm">
                                    <b>{activityData.email}</b> — лимит:{' '}
                                    {activityData.daily_limit === 0
                                        ? 'безлимит'
                                        : `${activityData.daily_limit} вопр./день`}
                                    , тариф: {activityData.plan_type}
                                </Typography>
                                {activityData.days?.length === 0 && (
                                    <Typography level="body-sm" color="neutral">Нет активности за 30 дней</Typography>
                                )}
                                {activityData.days?.map(day => {
                                    // Danger chip only when limit is set (> 0) AND exceeded.
                                    const overLimit =
                                        activityData.daily_limit > 0 &&
                                        day.questions > activityData.daily_limit;
                                    return (
                                        <Sheet key={day.date} variant="outlined" sx={{ p: 1.5, borderRadius: 'md' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography level="body-sm" fontWeight="bold">{day.date}</Typography>
                                                <Chip
                                                    size="sm"
                                                    color={overLimit ? 'danger' : 'success'}
                                                    variant="soft"
                                                >
                                                    {day.questions} вопросов
                                                </Chip>
                                            </Stack>
                                            <Stack spacing={0.3} sx={{ mt: 0.5 }}>
                                                {day.tests.map((t, i) => (
                                                    <Typography key={i} level="body-xs" color="neutral">
                                                        {t.time} — {t.title} ({t.questions}q)
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Sheet>
                                    );
                                })}

                                {/* Module open history (30 days) — wired to ModuleView rows
                                    created in modules/views.py:get_module_detail. */}
                                {activityData.module_views && activityData.module_views.length > 0 && (
                                    <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mt: 1 }}>
                                        <Typography level="title-sm" sx={{ mb: 1 }}>
                                            Открытые модули (30 дней) — {activityData.module_views.length}
                                        </Typography>
                                        <Stack spacing={0.4}>
                                            {activityData.module_views.map((m, i) => (
                                                <Typography key={i} level="body-xs" color="neutral">
                                                    {m.date} {m.time} —{' '}
                                                    <a
                                                        href={`/modules/${m.module_id}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{ color: '#DA7756', textDecoration: 'none' }}
                                                    >
                                                        {m.title}
                                                    </a>
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Sheet>
                                )}
                                {activityData.module_views && activityData.module_views.length === 0 && (
                                    <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                                        Пока не открывала ни одного модуля за 30 дней
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
